import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage, defineMessage, useIntl } from 'react-intl';
import { Outlet, useNavigate } from 'react-router';

import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { FullWidthContainer } from '@sorare/core/src/atoms/layout/FullWidthContainer';
import { HeadlineL } from '@sorare/core/src/atoms/typography';
import { PageHeader } from '@sorare/core/src/components/navigation/PageHeader';
import { ANY_SPORT_REWARDS_FIXTURE_LEAGUE_TIER_RARITY } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { fantasy } from '@sorare/core/src/lib/glossary';
import {
  getPlayerPoolTree,
  getPlayerPoolTreeSelection,
} from '@sorare/core/src/lib/rewards';
import { Tree } from '@sorare/core/src/routing/Tree';

import { PoolRarityToggle } from '#src/components/rewards/PoolRarityToggle/index.tsx';

import type {
  RewardsFixtureLayoutQuery,
  RewardsFixtureLayoutQueryVariables,
} from './__generated__/index.graphql.ts';

const REWARDS_FIXTURE_LAYOUT_QUERY = gql`
  query RewardsFixtureLayoutQuery($fixture: String!) {
    so5 {
      so5Fixture(slug: $fixture) {
        slug
        startDate
        endDate
        ...getPlayerPoolTree_so5Fixture
      }
    }
  }
  ${getPlayerPoolTree.fragments.so5Fixture}
` as TypedDocumentNode<
  RewardsFixtureLayoutQuery,
  RewardsFixtureLayoutQueryVariables
>;

type Props = {
  fixture: string;
  league?: string;
  tier?: string;
  rarity?: string;
};

export const RewardsFixtureLayout = ({
  fixture,
  league,
  tier,
  rarity,
}: Props) => {
  const { data } = useQuery(REWARDS_FIXTURE_LAYOUT_QUERY, {
    variables: {
      fixture,
    },
  });
  const { so5Fixture } = data?.so5 || {};

  const navigate = useNavigate();
  const { formatDate } = useIntl();
  const { generateSportContextPath } = useSportContext();

  if (!so5Fixture) {
    return null;
  }

  const { startDate, endDate } = so5Fixture;

  const { schema, labels } = getPlayerPoolTree(so5Fixture);

  const selected = getPlayerPoolTreeSelection({ schema, league, tier, rarity });

  return (
    <FullWidthContainer>
      <PageHeader
        text={defineMessage({
          defaultMessage: 'Players pool',
        })}
      />
      <Tree
        title={
          <HeadlineL as="p">
            <FormattedMessage
              {...fantasy.gameWeek}
              values={{
                gameWeek: (
                  <>
                    {formatDate(startDate, { month: 'short' })}
                    &nbsp;
                    {formatDate(startDate, { day: 'numeric' })}-
                    {formatDate(endDate, { day: 'numeric' })}
                  </>
                ),
              }}
            />
          </HeadlineL>
        }
        schema={schema}
        labels={labels}
        selected={selected}
        renderSub2={({ options, selected: selectedOption, onSelect }) => {
          const rarities = options.filter(
            (option): option is Rarity => option in Rarity
          );

          return (
            <PoolRarityToggle
              rarities={rarities}
              selectedRarity={
                rarities.find(r => r === selectedOption) || rarities[0]
              }
              onChange={onSelect}
            />
          );
        }}
        onSelect={([selectedLeague, selectedTier, selectedRarity]) => {
          if (!selectedLeague || !selectedTier || !selectedRarity) {
            return;
          }
          navigate(
            generateSportContextPath(
              ANY_SPORT_REWARDS_FIXTURE_LEAGUE_TIER_RARITY,
              {
                params: {
                  fixture: so5Fixture.slug,
                  league: selectedLeague,
                  tier: selectedTier,
                  rarity: selectedRarity,
                },
              }
            ),
            {
              replace: true,
            }
          );
        }}
      >
        <Outlet />
      </Tree>
    </FullWidthContainer>
  );
};
